import NhlLegacyTeamStackingMarker from 'components/organisms/nhl-legacy-team-stacking/services/nhl-legacy-team-stacking-marker';
import {getPlayersGroupUID, getSimplexUnitName, getPlayersSubGroupUID} from 'helpers/simplex-helper';
import Const from 'constants';
import {countEntries, getRandomArray} from 'helpers/array-helper';
import {calcAndRoundDownValByPct, calcAndRoundUpValByPct} from "helpers/common-helper";
import RuleBuilder from 'services/simplex/rule-builder';
import {getCheckedNumbers} from "../helpers";

function getGroupName(teamName, stackNumber) {
    const groupName = getPlayersGroupUID(teamName, stackNumber);

    return NhlLegacyTeamStackingMarker.setSuffix(groupName);
}

function getSubGroupName(teamName, stackNumber, subGroupKey, subGroupNumber) {
    const subGroupName = getPlayersSubGroupUID(teamName, stackNumber, subGroupKey, subGroupNumber);

    return NhlLegacyTeamStackingMarker.setSuffix(subGroupName);
}

function createPreparedDoubleTeamsConstraints(params) {
    const {constraints} = params;

    const result = [];

    if (isEmpty(constraints)) {
        return result;
    }

    const teamGroups = {};
    Object.values(constraints).map(stackTeams => {
        Object.values(stackTeams).map(stackTeam => {
            teamGroups[stackTeam.teamName] ??= [];
            teamGroups[stackTeam.teamName].push(stackTeam.groupName);
        });
    });

    Object.values(teamGroups).map(teamGroup => {
        if (teamGroup.length >= 2) {
            const ruleBuilder = RuleBuilder.fromAtMost(1);
            teamGroup.map(groupName => ruleBuilder.addVar(groupName, 1));

            result.push(ruleBuilder.createRule());
        }
    });

    return [...result];
}

function createConstraintsForExcludeTeamsCombinations(params) {
    const {
        lineupTeamsCombinations,
        rawLineups,
        lineupsQuantity,
        constraints,
    } = params;
    const items = lineupTeamsCombinations.exposureMax.all();

    const result = [];

    if (isEmpty(constraints)) {
        return result;
    }

    const firstStackConstraints = constraints[Const.NHL_LEGACY_TEAM_STACKING_FIRST_STACK];
    const secondStackConstraints = constraints[Const.NHL_LEGACY_TEAM_STACKING_SECOND_STACK];
    const thirdStackConstraints = constraints[Const.NHL_LEGACY_TEAM_STACKING_THIRD_STACK];

    Object.values(items).map(item => {

        let usageQuantity = 0;
        rawLineups.asArr().map(lineup => {
            const diff = arrayDiff(item.teamsCombination, lineup.getTeamNames());

            isEmpty(diff) && usageQuantity++;
        });

        const expMax = item.value;
        const usagePercent = (usageQuantity + 1) / lineupsQuantity.getLineupsQuantity() * 100;

        if (usagePercent <= expMax) {
            return;
        }

        const [team1, team2, team3] = item.teamsCombination;

        const teamCombinationsMap = [
            [team1, team2, team3],
            [team1, team3, team2],
            [team2, team1, team3],
            [team2, team3, team1],
            [team3, team1, team2],
            [team3, team2, team1],
        ];

        teamCombinationsMap.map(teamCombination => {
            const [team1, team2, team3] = teamCombination;

            if (team1 in firstStackConstraints && team2 in secondStackConstraints && team3 in thirdStackConstraints) {
                const ruleBuilder = RuleBuilder.fromAtMost(2,{
                    [firstStackConstraints[team1].groupName]: 1,
                    [secondStackConstraints[team2].groupName]: 1,
                    [thirdStackConstraints[team3].groupName]: 1,
                });

                result.push(ruleBuilder.createRule());
            }
        });
    });

    return [...result];
}

function createPreparedGroupsConstraints(params) {
    const {constraints} = params;

    const result = [];

    Object.values(constraints).map(group => {
        if (isEmpty(group)) {
            return;
        }

        const simplexVars = {};
        Object.values(group).map(item => simplexVars[item.groupName] = 1);

        if (!isEmpty(simplexVars)) {
            const ruleBuilder = RuleBuilder.fromExactly(1, simplexVars);

            result.push(ruleBuilder.createRule());
        }
    });

    return [...result];
}

function createConstraintsForIncludedTeamsByExposureMin(params) {
    const {
        constraints,
        lineupsQuantity,
        usedGroups,
        stacking,
    } = params;

    const result = [];

    const takenTeamNames = [];
    Object.keys(constraints).map(groupNumber => {
        const group = constraints[groupNumber];

        if (isEmpty(group)) {
            return;
        }

        const simplexVars = {};
        Object.values(group).map(item => {
            const expMin = stacking.getTeamMinExposureInStackNumber(item.teamName, groupNumber);
            const reservedQuantity = calcAndRoundDownValByPct(lineupsQuantity.getLineupsQuantity(), expMin);
            const usageQuantity = countEntries(item.groupName, usedGroups);
            const canTeamBeTaken = 0 < (reservedQuantity - usageQuantity) && !inArray(item.teamName, takenTeamNames);

            if (canTeamBeTaken) {
                simplexVars[item.groupName] = 1;
                takenTeamNames.push(item.teamName);
            }
        });

        if (isEmpty(simplexVars)) {
            return;
        }

        const ruleBuilder = RuleBuilder.fromAtLeast(1, simplexVars);

        result.push(ruleBuilder.createRule());
    });

    return [...result];
}

function createObjectiveForRandomlyChosenPlayers(params) {
    const {
        mappedPlayersByTeams,
        constraints,
        projectionPointsPropName,
    } = params;

    function getAvailableTeamNames(constraints) {
        let availableTeamNames = [];
        Object.values(constraints).map(groupConstraints => {
            availableTeamNames = [
                ...availableTeamNames,
                ...Object.keys(groupConstraints),
            ];
        });

        return [...new Set(availableTeamNames)];
    }

    const availableTeamNames = getAvailableTeamNames(constraints);
    const randomTeams = getRandomArray(availableTeamNames, 2);

    const result = {};
    randomTeams.map(teamName => {
        const availablePlayers = mappedPlayersByTeams[teamName];
        const randomPlayers = getRandomArray(availablePlayers, 3);

        randomPlayers.map(player => {
            const pointsValue = player[projectionPointsPropName];
            const projectionPoints = pointsValue * Const.NHL_LEGACY_TEAM_STACKING_PROJECTION_MULTIPLY_VALUE;

            result[getSimplexUnitName(player.playerId)] = parseInt(projectionPoints);
        });
    });

    return {...result};
}

function createPreparedTeamsConstraints(params) {
    const {stacking, mappedPlayersByTeams, sportConfig} = params;
    const stacks = stacking.getStacks();
    const combinations = stacking.getCheckedCombinations();

    const constraints = {};

    Object.keys(stacks).map(stackNumber => {
        const teams = stacking.getActiveAndCheckedTeamsByStackNumber(stackNumber);

        Object.keys(teams).map(teamName => {
            const {lineGroups, ppLines} = teams[teamName];
            const availableLineGroups = getCheckedNumbers(lineGroups);
            const availablePPLines = getCheckedNumbers(ppLines);

            if (isEmpty(availableLineGroups) && isEmpty(availablePPLines)) {
                return;
            }

            const teamLineGroupConstraints = {};
            availableLineGroups.map(lineGroup => {
                const constraints = createConstraintsForTeam(teamName, function (player) {
                    return !player.isGoalie() && Number(player.userLineGroup) === lineGroup;
                });

                !isEmpty(constraints) && (teamLineGroupConstraints[lineGroup] = constraints);
            });

            const teamPpLineConstraints = {};
            availablePPLines.map(ppLine => {
                const constraints = createConstraintsForTeam(teamName, function (player) {
                    return !player.isGoalie() && Number(player.userPpLine) === ppLine;
                });

                !isEmpty(constraints) && (teamPpLineConstraints[ppLine] = constraints);
            });

            if (isEmpty(teamLineGroupConstraints) && isEmpty(teamPpLineConstraints)) {
                return;
            }

            const defensemanTeamLineGroupConstraintRows = {};
            availableLineGroups.map(lineGroup => {
                const constraints = createConstraintsRowForTeam(teamName, function (player) {
                    return player.isDefenseman() && Number(player.userLineGroup) === lineGroup;
                });

                !isEmpty(constraints) && (defensemanTeamLineGroupConstraintRows[lineGroup] = constraints);
            });

            const defensemanTeamPpLineConstraintRows = {};
            availablePPLines.map(ppLine => {
                const constraints = createConstraintsRowForTeam(teamName, function (player) {
                    return player.isDefenseman() && Number(player.userPpLine) === ppLine;
                });

                !isEmpty(constraints) && (defensemanTeamPpLineConstraintRows[ppLine] = constraints);
            });

            constraints[stackNumber] ??= {};
            constraints[stackNumber][teamName] = {
                teamLineGroupConstraints,
                teamPpLineConstraints,
                defensemanTeamLineGroupConstraintRows,
                defensemanTeamPpLineConstraintRows,
                goalieTeamConstraintRow: createGoalieConstraintRowForTeam(teamName),
            };
        });
    });

    const result = {};
    Object.keys(combinations).map(comboKey => {
        const combo = combinations[comboKey];
        const stackConstraints = {};

        Object.keys(constraints).map(stackNumber => {
            Object.keys(constraints[stackNumber]).map(teamName => {
                const comboStackConstraints = createComboStackConstraints({stacking, combo, constraints, stackNumber, teamName});

                if (isEmpty(comboStackConstraints)) {
                    return;
                }

                stackConstraints[stackNumber] ??= {};
                stackConstraints[stackNumber][teamName] = comboStackConstraints;
            });
        });

        isStackConstraintsValid(stackConstraints, combo) && (result[comboKey] = stackConstraints);
    });

    function createComboStackConstraints(params) {
        const {stacking, combo, constraints, stackNumber, teamName} = params;
        const {
            teamLineGroupConstraints,
            teamPpLineConstraints,
            defensemanTeamLineGroupConstraintRows,
            defensemanTeamPpLineConstraintRows,
            goalieTeamConstraintRow,
        } = constraints[stackNumber][teamName];
        const groupName = getGroupName(teamName, stackNumber);
        const playersQuantity = combo.playersQuantity[stackNumber];
        const isDefensemanMandatory = stacking.isDefensemanMandatoryInCombinationStack(combo, stackNumber);
        const stackConstraints = [];
        const subGroupNames = [];

        function createStackConstraints(params) {
            const {teamConstraints, constraintKey, playersQuantity, subGroupName, defensemanTeamConstraintRows} = params;
            const row = cp(teamConstraints[constraintKey].row);
            const isPlayersQuantityNotEnoughToCreateRule = playersQuantity > Object.keys(row).length;

            if (isPlayersQuantityNotEnoughToCreateRule) {
                return [];
            }

            const defensemanTeamConstraints = createDefensemanConstraints({
                sportConfig,
                defensemanTeamConstraintRow: defensemanTeamConstraintRows[constraintKey] || {},
                subGroupName,
                isDefensemanMandatory,
            });

            if (isDefensemanMandatory && isEmpty(defensemanTeamConstraints)) {
                return [];
            }

            row[subGroupName] = -1 * playersQuantity;
            const stackConstraints = [{...teamConstraints[constraintKey], row}];

            if (!isEmpty(defensemanTeamConstraints)) {
                defensemanTeamConstraints.map(constraint => stackConstraints.push({...constraint}));
            }

            return stackConstraints;
        }

        Object.keys(teamLineGroupConstraints).map(lineGroup => {
            const subGroupName = getSubGroupName(teamName, stackNumber, 'LG', lineGroup);

            const constraints = createStackConstraints({
                teamConstraints: teamLineGroupConstraints,
                constraintKey: lineGroup,
                playersQuantity,
                subGroupName,
                defensemanTeamConstraintRows: defensemanTeamLineGroupConstraintRows,
            });

            if (isEmpty(constraints)) {
                return;
            }

            subGroupNames.push(subGroupName);
            constraints.map(constraint => stackConstraints.push({...constraint}));
        });

        Object.keys(teamPpLineConstraints).map(ppLine => {
            const subGroupName = getSubGroupName(teamName, stackNumber, 'PL', ppLine);

            const constraints = createStackConstraints({
                teamConstraints: teamPpLineConstraints,
                constraintKey: ppLine,
                playersQuantity,
                subGroupName,
                defensemanTeamConstraintRows: defensemanTeamPpLineConstraintRows,
            });

            if (isEmpty(constraints)) {
                return;
            }

            subGroupNames.push(subGroupName);
            constraints.map(constraint => stackConstraints.push({...constraint}));
        });

        const subGroupsConstraints = createSubGroupsConstraints({sportConfig, subGroupNames, groupName});

        if (isEmpty(subGroupsConstraints)) {
            return {};
        }

        subGroupsConstraints.map(subGroupsConstraint => stackConstraints.push({...subGroupsConstraint}));

        const goalieTeamConstraints = createGoalieConstraints({
            sportConfig,
            goalieTeamConstraintRow,
            groupName,
            playersQuantity,
        });

        !isEmpty(goalieTeamConstraints) && stackConstraints.push({...goalieTeamConstraints});

        return {
            teamName,
            groupName,
            subGroupNames,
            playersQuantity,
            constraints: stackConstraints,
        };
    }

    function isStackConstraintsValid(stackConstraints, combo) {
        const stacksWithConstraintsQuantity = Object.keys(stackConstraints).length;
        const comboStacksQuantity = Object.keys(combo.playersQuantity).length;

        return stacksWithConstraintsQuantity >= comboStacksQuantity;
    }

    function createConstraintsForTeam(teamName, isPlayerValidFunc) {
        const simplexVars = createConstraintsRowForTeam(teamName, isPlayerValidFunc);

        if (isEmpty(simplexVars)) {
            return {};
        }

        const ruleBuilder = RuleBuilder.fromAtLeast(0, simplexVars);

        return ruleBuilder.createRule();
    }

    function createDefensemanConstraints(params) {
        const {sportConfig, defensemanTeamConstraintRow, subGroupName, isDefensemanMandatory} = params;

        const constraints = [];

        if (isEmpty(defensemanTeamConstraintRow)) {
            return constraints;
        }

        if (isDefensemanMandatory) {
            const atLeastConstraintRow = cp(defensemanTeamConstraintRow);
            atLeastConstraintRow[subGroupName] = -1;

            const ruleBuilder = RuleBuilder.fromAtLeast(0, atLeastConstraintRow);

            constraints.push(ruleBuilder.createRule());
        }

        const playersInTeam = sportConfig.getConfigProp('playersInTeam', 0);
        const atMostConstraintRow = cp(defensemanTeamConstraintRow);
        atMostConstraintRow[subGroupName] = playersInTeam - 1;

        const ruleBuilder = RuleBuilder.fromAtMost(playersInTeam, atMostConstraintRow);

        constraints.push(ruleBuilder.createRule());

        return constraints;
    }

    function createGoalieConstraints(params) {
        const {sportConfig, goalieTeamConstraintRow, groupName, playersQuantity} = params;
        const isGoalieAllowed = Number(playersQuantity) < 3;

        if (isEmpty(goalieTeamConstraintRow) || isGoalieAllowed) {
            return {};
        }

        const playersInTeam = sportConfig.getConfigProp('playersInTeam', 0);

        const simplexVars = cp(goalieTeamConstraintRow);
        simplexVars[groupName] = playersInTeam;

        const ruleBuilder = RuleBuilder.fromAtMost(playersInTeam, simplexVars);

        return ruleBuilder.createRule();
    }

    function createSubGroupsConstraints(params) {
        const {sportConfig, subGroupNames, groupName} = params;
        const constraints = [];

        if (isEmpty(subGroupNames)) {
            return constraints;
        }

        const atLeastRuleBuilder = RuleBuilder.fromAtLeast(0);
        atLeastRuleBuilder.addVar(groupName, -1)
        subGroupNames.map(subGroupName => atLeastRuleBuilder.addVar(subGroupName, 1));

        constraints.push(atLeastRuleBuilder.createRule());

        const playersInTeam = sportConfig.getConfigProp('playersInTeam', 0);

        const atMostRuleBuilder = RuleBuilder.fromAtMost(playersInTeam);
        atMostRuleBuilder.addVar(groupName, playersInTeam - 1)
        subGroupNames.map(subGroupName => atMostRuleBuilder.addVar(subGroupName, 1));

        constraints.push(atMostRuleBuilder.createRule());

        return constraints;
    }

    function createConstraintsRowForTeam(teamName, isPlayerValidFunc) {
        if (!mappedPlayersByTeams.hasOwnProperty(teamName)) {
            return {};
        }

        const simplexVars = {};
        mappedPlayersByTeams[teamName].map(player => {
            isPlayerValidFunc(player) && (simplexVars[getSimplexUnitName(player.getId())] = 1);
        });

        return {...simplexVars};
    }

    function createGoalieConstraintRowForTeam(teamName) {
        if (!mappedPlayersByTeams.hasOwnProperty(teamName)) {
            return {};
        }

        const simplexVars = {};
        mappedPlayersByTeams[teamName]
            .filter(player => player.isGoalie())
            .map(player => simplexVars[getSimplexUnitName(player.getId())] = 1);

        return {...simplexVars};
    }

    return {...result};
}

function createConstraintsForExcludedTeamsByExposureMax(params) {
    const {
        teamsConstraints,
        lineupsQuantity,
        usedGroups,
        stacking,
    } = params;

    let result = cp(teamsConstraints);

    Object.keys(teamsConstraints).map(groupNumber => {
        const group = teamsConstraints[groupNumber];

        Object.keys(group).map(key => {
            const team = group[key];
            const expMax = stacking.getTeamMaxExposureInStackNumber(team.teamName, groupNumber);
            const usageQuantity = countEntries(team.groupName, usedGroups);
            const usagePercent = (usageQuantity + 1) / lineupsQuantity.getLineupsQuantity() * 100;

            if (usagePercent > expMax) {
                delete result[groupNumber][key];
            }
        });
    });

    return {...result};
}

function buildCombinationsQuantity(params) {
    const {stacking, lineupsQuantity} = params;
    const combinations = stacking.getCheckedCombinations();

    const result = [];
    Object.keys(combinations).map(comboKey => {
        const {exposure} = combinations[comboKey];

        const minLineupsQuantity = calcAndRoundUpValByPct(lineupsQuantity.getLineupsQuantity(), exposure.min);
        let maxLineupsQuantity = calcAndRoundDownValByPct(lineupsQuantity.getLineupsQuantity(), exposure.max);

        if (maxLineupsQuantity < minLineupsQuantity) {
            maxLineupsQuantity = minLineupsQuantity;
        }

        result.push({
            comboKey,
            minLineupsQuantity,
            maxLineupsQuantity,
        });
    });

    return [...result];
}

class ConstraintsBuilder {
    constructor(dependencies) {
        this.dependencies = {...dependencies};
    }

    createObjectiveForRandomlyChosenPlayers(params) {
        const {mappedPlayersByTeams} = this.dependencies;
        const {constraints, projectionPointsPropName} = params;

        return createObjectiveForRandomlyChosenPlayers({
            constraints,
            mappedPlayersByTeams,
            projectionPointsPropName,
        });
    }

    createConstraintsForExcludeTeamsCombinations(params) {
        return createConstraintsForExcludeTeamsCombinations(params);
    }

    createPreparedGroupsConstraints(params) {
        return createPreparedGroupsConstraints(params);
    }

    createConstraintsForIncludedTeamsByExposureMin(params) {
        const {stacking} = this.dependencies;

        return createConstraintsForIncludedTeamsByExposureMin({
            stacking,
            ...params,
        });
    }

    createConstraintsForExcludedTeamsByExposureMax(params) {
        const {stacking} = this.dependencies;

        return createConstraintsForExcludedTeamsByExposureMax({
            stacking,
            ...params,
        });
    }

    createPreparedDoubleTeamsConstraints(params) {
        return createPreparedDoubleTeamsConstraints(params);
    }

    createPreparedTeamsConstraints(params) {
        const {stacking, mappedPlayersByTeams, sportConfig} = this.dependencies;

        return createPreparedTeamsConstraints({
            sportConfig,
            stacking,
            mappedPlayersByTeams,
            ...params,
        });
    }

    buildCombinationsQuantity(params) {
        const {stacking} = this.dependencies;
        
        return buildCombinationsQuantity({stacking, ...params});
    }
}

export {
    ConstraintsBuilder,
};
